import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { SparkleAlertService } from '@sparkle-ui/core';
import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthState } from 'src/app/auth/auth.state';
import { ENVIRONMENT_TOKEN } from 'src/environments/environment-token';
import { Me, Organization } from '../openapi';

let refreshRequest: Observable<[Me, Organization[]]> | null = null;

export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthState);
  const env = inject(ENVIRONMENT_TOKEN);
  const sparkleAlertService = inject(SparkleAlertService);
  const isRefreshRequest = req.url === `${env.baseUrl}/token/refresh`;

  let _req = req;

  if (auth.token() && req.url.startsWith(env.baseUrl)) {
    _req = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${auth.token()}`),
    });
  }

  return next(_req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || (isRefreshRequest && (error.status === 403 || error.status === 400))) {
        if (isRefreshRequest) {
          const redirectPath =
            location.pathname !== '/auth' && location.pathname !== '/failed'
              ? `/auth?redirecturl=${encodeURIComponent(location.href)}`
              : `/auth?redirecturl=${encodeURIComponent(`${location.origin}/`)}`;
          auth.logout();
          window.location.href = redirectPath;
        } else {
          refreshRequest ??= auth.refreshToken().pipe(shareReplay());

          return refreshRequest.pipe(
            switchMap(() => {
              return next(
                req.clone({
                  headers: req.headers.set('Authorization', `Bearer ${auth.token()}`),
                })
              );
            }),
            catchError((error) => {
              return throwError(() => {
                return {
                  error,
                  message: error.message,
                };
              });
            }),
            finalize(() => {
              refreshRequest = null;
            })
          );
        }
      } else {
        let errorMessage = error.message;
        if (error.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.status === 404) {
          errorMessage = 'Not found.';
        } else if (error.status === 500) {
          errorMessage = 'Internal server error.';
        } else if (error.status === 0) {
          errorMessage = 'Network error. Please check your connection.';
        }

        // If the server returns a specific error message
        // (e.g., from a validation error), use that message
        if (error.error?.error?.message) {
          errorMessage = error.error.error.message;
        }

        sparkleAlertService.error(errorMessage);
      }

      const errorMsg = `Error Code: ${error.status}, Message: ${error.message}`;

      return throwError(() => {
        return {
          error,
          message: errorMsg,
        };
      });
    })
  );
};
